import { Radio, Gift, Monitor, Smartphone, Tablet, Laptop, Globe } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";

export function Stat({ label, value, accent = "text-[#22D3EE]", testid }) {
  return (
    <div className="hq-glass rounded-xl px-4 py-3 border border-white/10" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono-data">{label}</p>
      <p className={`font-display text-3xl mt-1 ${accent}`}>{value}</p>
    </div>
  );
}

function Bars({ rows, labelKey, testid, icon }) {
  if (!rows?.length) return <p className="text-sm text-muted-foreground py-6 text-center">No data yet.</p>;
  const total = rows.reduce((s, r) => s + r.views, 0) || 1;
  return (
    <div className="space-y-2" data-testid={testid}>
      {rows.map((r) => {
        const pct = Math.round((r.views / total) * 100);
        return (
          <div key={r[labelKey] ?? "none"} className="flex items-center gap-3 text-sm">
            <div className="w-36 truncate flex items-center gap-1.5 text-slate-200">{icon?.(r[labelKey])}{r[labelKey] || "Visitor (signed out)"}</div>
            <div className="flex-1 h-5 rounded bg-white/5 overflow-hidden"><div className="h-full bg-gradient-to-r from-[#22D3EE] to-primary" style={{ width: `${pct}%` }} /></div>
            <div className="w-28 text-right text-xs text-slate-400">{r.views} views · {pct}%</div>
          </div>
        );
      })}
    </div>
  );
}

const deviceIcon = (d) => d === "Mobile" ? <Smartphone className="w-3.5 h-3.5" /> : d === "Tablet" ? <Tablet className="w-3.5 h-3.5" /> : d === "Chromebook" ? <Laptop className="w-3.5 h-3.5" /> : d === "Desktop" ? <Monitor className="w-3.5 h-3.5" /> : <Globe className="w-3.5 h-3.5" />;

export function TrafficPanel({ traffic, days }) {
  const t = traffic.totals;
  const f = traffic.trial_funnel;
  const nvr = traffic.new_vs_returning;
  return (
    <div className="space-y-6" data-testid="traffic-panel">
      <div className="flex items-center gap-2 text-sm">
        <span className="relative flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#34D399] opacity-75" /><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#34D399]" /></span>
        <Radio className="w-4 h-4 text-[#34D399]" /><span className="font-mono-data text-[#34D399]" data-testid="live-visitors">{t.live_visitors}</span><span className="text-muted-foreground">visitor{t.live_visitors === 1 ? "" : "s"} active right now</span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Views today" value={t.views_today} testid="stat-views-today" />
        <Stat label="Views (7d)" value={t.views_7d} />
        <Stat label="Unique visitors (7d)" value={t.unique_7d} accent="text-[#34D399]" />
        <Stat label="Unique visitors (30d)" value={t.unique_30d} accent="text-[#34D399]" />
        <Stat label="Views (30d)" value={t.views_30d} accent="text-slate-100" />
        <Stat label="Sessions (30d)" value={t.sessions_30d} accent="text-slate-100" />
        <Stat label="All-time views" value={t.all_time_views} accent="text-primary" testid="stat-all-time" />
        <Stat label="All-time visitors" value={t.unique_all_time} accent="text-primary" />
      </div>

      <section className="hq-glass rounded-2xl p-5 border border-primary/40" data-testid="trial-funnel">
        <h3 className="font-display text-xl flex items-center gap-2"><Gift className="w-5 h-5 text-primary" /> "Try it free" funnel · last {days} days</h3>
        <div className="grid grid-cols-3 gap-3 mt-4 text-center">
          <div><p className="font-display text-3xl text-[#22D3EE]" data-testid="funnel-landing">{f.landing_visitors}</p><p className="text-xs text-muted-foreground">landing-page visitors</p></div>
          <div><p className="font-display text-3xl text-primary" data-testid="funnel-signups">{f.trial_signups}</p><p className="text-xs text-muted-foreground">trial signups ({f.trial_signups_total} all-time)</p></div>
          <div><p className="font-display text-3xl text-[#34D399]" data-testid="funnel-rate">{f.conversion_rate}%</p><p className="text-xs text-muted-foreground">visitor → trial</p></div>
        </div>
      </section>

      <section className="hq-glass rounded-2xl p-5 border border-white/10">
        <h3 className="font-display text-xl">New vs returning visitors</h3>
        <div className="grid grid-cols-3 gap-3 mt-3">
          {[["Today", nvr.today], ["Last 7 days", nvr.last_7d], ["Last 30 days", nvr.last_30d]].map(([label, v]) => (
            <div key={label} className="rounded-xl bg-white/[0.03] border border-white/10 p-3">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono-data">{label}</p>
              <p className="text-sm mt-1"><span className="text-[#22D3EE] font-mono-data">{v.new}</span> new · <span className="text-primary font-mono-data">{v.returning}</span> returning</p>
              <p className="text-xs text-slate-400">{v.return_rate}% came back</p>
            </div>
          ))}
        </div>
      </section>

      <section className="hq-glass rounded-2xl p-5 border border-white/10" data-testid="daily-chart">
        <h3 className="font-display text-xl mb-3">Daily traffic · last {days} days</h3>
        <div className="h-52">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={traffic.daily_series}>
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94a3b8" }} tickFormatter={(d) => d.slice(5)} interval="preserveStartEnd" />
              <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#94a3b8" }} width={28} />
              <Tooltip contentStyle={{ background: "#0b1220", border: "1px solid rgba(255,255,255,0.1)", fontSize: 12 }} />
              <Bar dataKey="views" fill="#22D3EE" radius={[3, 3, 0, 0]} name="Views" />
              <Bar dataKey="unique_visitors" fill="#FB923C" radius={[3, 3, 0, 0]} name="Unique visitors" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      <div className="grid gap-6 lg:grid-cols-2">
        <section className="hq-glass rounded-2xl p-5 border border-white/10"><h3 className="font-display text-xl mb-3">Where visitors came from</h3><Bars rows={traffic.top_referrers} labelKey="source" testid="top-referrers" /></section>
        <section className="hq-glass rounded-2xl p-5 border border-white/10"><h3 className="font-display text-xl mb-3">Devices</h3><Bars rows={traffic.device_breakdown} labelKey="device" testid="device-breakdown" icon={deviceIcon} /></section>
        <section className="hq-glass rounded-2xl p-5 border border-white/10"><h3 className="font-display text-xl mb-3">Who's viewing</h3><Bars rows={traffic.roles} labelKey="role" testid="role-breakdown" /></section>
        <section className="hq-glass rounded-2xl p-5 border border-white/10">
          <h3 className="font-display text-xl mb-3">Top pages</h3>
          {traffic.top_pages.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center">No page views yet.</p> : (
            <table className="w-full text-sm" data-testid="top-pages">
              <thead><tr className="text-[10px] uppercase tracking-widest text-muted-foreground"><th className="text-left py-1">Page</th><th className="text-right">Views</th><th className="text-right">Unique</th></tr></thead>
              <tbody>{traffic.top_pages.map((p) => <tr key={p.path} className="border-t border-white/5"><td className="py-1.5 font-mono-data text-xs">{p.path}</td><td className="text-right text-[#22D3EE]">{p.views}</td><td className="text-right text-[#34D399]">{p.unique_visitors}</td></tr>)}</tbody>
            </table>
          )}
        </section>
      </div>
    </div>
  );
}
